import { AlarmClock, Bookmark, BookmarkFilled, Copy, CornerUpLeft, Flag, Forward, Hash, History, MoreHorizontal, Pencil, Pin, PinOff, SmilePlus, Threads, Trash2, UserX } from '@/components/ui/icons';
import { memo, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { renderMentionsAsText, type ForwardedMessageDTO, type MessageDTO } from '@clanfire/shared';
import { StaffBadge } from '@/components/profile/StaffBadge';
import { StyledName } from '@/components/profile/StyledName';
import { Avatar } from '@/components/ui/Avatar';
import { Menu, Tooltip, type MenuEntry } from '@/components/ui/Popover';
import { QUICK_REACTIONS } from '@/lib/emoji';
import { decodeForEditing, encodeComposerText } from '@/lib/compose';
import { deleteMessage, editMessage, retryMessage, setPinned, toggleReaction } from '@/lib/messaging';
import { formatReminder, toggleSaved } from '@/lib/saved';
import { AVATAR_SIZES } from '@/lib/appearance';
import { extractThemeCodes } from '@/lib/themes';
import { formatClock, formatLong, formatMessageTime } from '@/lib/time';
import { cn, copyText } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useSwipeToReply, SWIPE_REPLY_PX } from '@/hooks/useSwipeToReply';
import { useBlocks, useIsBlocked } from '@/stores/blocks';
import { useSavedState } from '@/stores/saved';
import { keyOfMessage, useMessages, type ClientMessage } from '@/stores/messages';
import { memberName, roleColor, useMemberName, useMemberRoles, useServerLook, useServers } from '@/stores/servers';
import { toast, useUi } from '@/stores/ui';
import { useUser, useUsers } from '@/stores/users';
import { Attachments } from './Attachments';
import { EmojiGlyph, emojiLabel } from './CustomEmoji';
import { EmojiPicker } from './EmojiPicker';
import { extractGiftCodes, GiftEmbed } from './GiftEmbed';
import { extractInviteCodes, InviteEmbed } from './InviteEmbed';
import { extractPreviewUrls, LinkEmbed } from './LinkEmbed';
import { isGifOnly } from '@/lib/gifs';
import { MessageContent } from './MessageContent';
import { PollCard } from './PollCard';
import { ThemeEmbed } from './ThemeEmbed';
import { ThreadChip } from './ThreadChip';

interface MessageItemProps {
  message: ClientMessage;
  grouped: boolean;
  compact: boolean;
  /** Phone layout: chat bubbles, yours on the right. */
  bubble?: boolean;
  myId: string;
  canManageMessages: boolean;
  canReact: boolean;
  /** Messages in a text channel you can write in can start a thread. */
  canStartThreads?: boolean;
  highlighted: boolean;
  onJump: (messageId: string) => void;
}

function useRoleColor(serverId: string | null, userId: string | undefined) {
  return roleColor(useMemberRoles(serverId, userId));
}

/** The author's highest role, shown as a small tag beside their name (not for the everyone role). */
function useRoleName(serverId: string | null, userId: string | undefined) {
  return useMemberRoles(serverId, userId)[0]?.name;
}

function RoleTag({ name }: { name: string }) {
  return <span className="shrink-0 self-center rounded-full bg-accent-2-soft px-2 text-[11px] leading-[18px] text-accent-2-ink">{name}</span>;
}

export const MessageItem = memo(function MessageItem({
  message,
  grouped,
  compact,
  bubble,
  myId,
  canManageMessages,
  canReact,
  canStartThreads,
  highlighted,
  onJump,
}: MessageItemProps) {
  const author = useUser(message.author?.id, message.author);
  // Their avatar for this server (Blaze), if they've set one.
  const authorLook = useServerLook(message.serverId, author);
  const authorName = useMemberName(message.serverId, message.author?.id) ?? author?.displayName ?? 'Deleted User';
  const roleColor = useRoleColor(message.serverId, message.author?.id);
  const roleName = useRoleName(message.serverId, message.author?.id);
  const openModal = useUi((s) => s.openModal);
  const setReplyTo = useUi((s) => s.setReplyTo);
  const editingId = useUi((s) => s.editing[keyOfMessage(message)]);
  const setEditing = useUi((s) => s.setEditing);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerAnchor = useRef<HTMLButtonElement>(null);
  const moreAnchor = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const authorBlocked = useIsBlocked(message.author?.id);
  const revealKey = `${keyOfMessage(message)}:${message.author?.id}`;
  const revealed = useBlocks((s) => !!s.revealed[revealKey]);
  const revealBlocked = useBlocks((s) => s.reveal);

  const key = keyOfMessage(message);
  const isMine = message.author?.id === myId;
  const [avatarSize, bubbleAvatar] = AVATAR_SIZES[useAuth((s) => s.settings?.layout.avatarSize ?? 'MEDIUM')];
  const isEditing = editingId === message.id;
  const local = message.pending || !!message.failed;
  const myRoleIds = useServers((s) => (message.serverId ? s.servers[message.serverId]?.me.roleIds : undefined));
  const mentionsMe =
    message.mentions.includes(myId) ||
    (message.replyTo?.author?.id === myId && message.replyPing && !isMine) ||
    (!isMine && message.mentionRoleIds.some((id) => myRoleIds?.includes(id))) ||
    (!isMine && message.mentionEveryone);
  // What screen readers hear: mentions and emoji as names, not raw tokens.
  const ariaText = useMemo(() => {
    const { users } = useUsers.getState();
    const { roles } = useServers.getState();
    const text = renderMentionsAsText(message.content, (id) => users[id]?.displayName, (id) => roles[id]?.name);
    return (text || (message.poll ? `Poll: ${message.poll.question}` : '')).slice(0, 80);
  }, [message.content, message.poll]);
  const showHeader = !grouped || !!message.replyTo;
  // Anyone in a DM may pin; servers need Manage Messages.
  const canPin = !local && (!!message.conversationId || canManageMessages);
  const canThread = !local && !!canStartThreads && !message.thread && !!message.channelId;
  const startThread = () => openModal({ type: 'createThread', channelId: message.channelId!, message });
  const savedState = useSavedState(message.id);
  const isSaved = savedState !== undefined;
  // What it said before an edit: for its author, moderators, and the people in a DM.
  const canSeeHistory = !!message.editedAt && !local && (isMine || canManageMessages || !!message.conversationId);
  const showHistory = canSeeHistory ? () => openModal({ type: 'editHistory', message }) : undefined;
  const swipe = useSwipeToReply(() => setReplyTo(key, message), !local && !isEditing);
  // The reply arrow slides in from the edge as the message is pulled.
  const swipeHint = swipe.pull > 0 && (
    <span
      aria-hidden
      className={cn('pointer-events-none absolute top-1/2 -right-9 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full transition-colors', swipe.pull >= SWIPE_REPLY_PX ? 'bg-accent text-accent-fg' : 'bg-sunken text-fg-muted')}
      style={{ opacity: Math.min(1, swipe.pull / SWIPE_REPLY_PX) }}
    >
      <CornerUpLeft size={16} />
    </span>
  );

  const openProfile = () => message.author && openModal({ type: 'profile', userId: message.author.id, serverId: message.serverId ?? undefined });

  const menuItems: MenuEntry[] = [
    canReact && !local && { label: 'Add reaction', icon: <SmilePlus size={16} />, onSelect: () => setPickerOpen(true) },
    !local && { label: 'Reply', icon: <CornerUpLeft size={16} />, onSelect: () => setReplyTo(key, message) },
    !local && { label: 'Forward…', icon: <Forward size={16} />, onSelect: () => openModal({ type: 'forward', message }) },
    canThread && { label: 'Create thread', icon: <Threads size={16} />, onSelect: startThread },
    isMine && !local && { label: 'Edit message', icon: <Pencil size={16} />, onSelect: () => setEditing(key, message.id) },
    canPin && {
      label: message.pinnedAt ? 'Unpin message' : 'Pin message',
      icon: message.pinnedAt ? <PinOff size={16} /> : <Pin size={16} />,
      onSelect: () => void setPinned(message, !message.pinnedAt),
    },
    !local && {
      label: isSaved ? 'Remove from saved' : 'Save message',
      icon: isSaved ? <BookmarkFilled size={16} /> : <Bookmark size={16} />,
      onSelect: () => void toggleSaved(message),
    },
    !local && { label: savedState ? 'Change reminder' : 'Remind me…', icon: <AlarmClock size={16} />, onSelect: () => openModal({ type: 'remindMe', message }) },
    canSeeHistory && { label: 'View edit history', icon: <History size={16} />, onSelect: () => openModal({ type: 'editHistory', message }) },
    {
      label: 'Copy text',
      icon: <Copy size={16} />,
      onSelect: async () => {
        const users = useUsers.getState().users;
        const ok = await copyText(renderMentionsAsText(message.content, (id) => memberName(message.serverId, id) ?? users[id]?.displayName));
        if (ok) toast.success('Copied to clipboard.');
        else toast.error('Could not access the clipboard.');
      },
    },
    !local && {
      label: 'Copy message ID',
      icon: <Hash size={16} />,
      onSelect: async () => {
        if (await copyText(message.id)) toast.success('Message ID copied.');
      },
    },
    (isMine || canManageMessages || !!message.author) && !local && 'separator',
    !isMine &&
      !local &&
      !!message.author && {
        label: 'Report message',
        icon: <Flag size={16} />,
        danger: true,
        onSelect: () => openModal({ type: 'report', messageId: message.id, subject: `${authorName}'s message` }),
      },
    (isMine || canManageMessages) && !local && { label: 'Delete message', icon: <Trash2 size={16} />, danger: true, onSelect: () => void deleteMessage(message) },
  ];

  const onContextMenu = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest('a, img, video, audio, textarea')) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  };

  const timestamp = (
    <time dateTime={message.createdAt} title={formatLong(message.createdAt)} className="text-xs text-fg-muted">
      {formatMessageTime(message.createdAt)}
    </time>
  );

  if (authorBlocked && !isMine && !revealed) {
    // One collapsed row per run of consecutive messages; "Show" reveals that author here.
    if (!showHeader) return <div id={`message-${message.id}`} hidden />;
    return (
      <article id={`message-${message.id}`} aria-label="Blocked messages" className="mt-[14px] flex items-center gap-2 px-4 py-1 text-sm text-fg-muted sm:px-6">
        <UserX size={16} className="shrink-0" />
        <span>Blocked messages</span>
        <span aria-hidden>·</span>
        <button type="button" onClick={() => revealBlocked(revealKey)} className="font-semibold text-link hover:underline">
          Show
        </button>
      </article>
    );
  }

  const reactions = message.reactions.length > 0 && (
    <div data-cf="reactions" className={cn('mt-1 flex flex-wrap gap-1', bubble && isMine && 'justify-end')}>
      {message.reactions.map((r) => (
        <ReactionChip key={r.emoji} message={message} emoji={r.emoji} userIds={r.userIds} mine={r.userIds.includes(myId)} canReact={canReact} />
      ))}
      {canReact && !bubble && (
        <Tooltip label="Add reaction">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            aria-label="Add reaction"
            className="flex h-7 items-center rounded-full px-2 text-fg-muted opacity-0 transition-opacity group-hover:opacity-100 hover:bg-hover focus:opacity-100"
          >
            <SmilePlus size={16} />
          </button>
        </Tooltip>
      )}
    </div>
  );

  const failedNote = message.failed && (
    <p className="mt-0.5 text-xs text-danger">
      {message.failed}{' '}
      <button type="button" className="font-semibold underline" onClick={() => retryMessage(message)}>
        Retry
      </button>{' '}
      ·{' '}
      <button type="button" className="font-semibold underline" onClick={() => useMessages.getState().removeLocal(key, message.id)}>
        Discard
      </button>
    </p>
  );

  const menus = (
    <>
      <Menu open={moreOpen} onClose={() => setMoreOpen(false)} anchor={moreAnchor} placement="bottom-end" items={menuItems} label="Message options" />
      <Menu open={!!menu} onClose={() => setMenu(null)} anchor={menu ?? { x: 0, y: 0 }} items={menuItems} label="Message options" />
      <EmojiPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        anchor={bubble ? bubbleRef : pickerAnchor.current ? pickerAnchor : moreAnchor}
        placement="bottom-end"
        serverId={message.serverId}
        onSelect={(e) => void toggleReaction(message, e)}
      />
    </>
  );

  // The hover toolbar (devices without hover use the long-press / right-click menu).
  const toolbar = !local && !isEditing && (
    <div
      role="toolbar"
      aria-label="Message actions"
      data-cf="message-toolbar"
      className={cn(
        'absolute -top-4 z-10 flex items-center rounded-full border border-line bg-elevated p-1 shadow-md transition-opacity',
        // Bubbles: away from your own messages, which sit on the right.
        bubble && isMine ? 'left-6' : 'right-6',
        pickerOpen || moreOpen ? 'opacity-100' : 'pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100',
      )}
    >
      {canReact &&
        QUICK_REACTIONS.slice(0, 3).map((emoji) => (
          <button key={emoji} type="button" onClick={() => void toggleReaction(message, emoji)} aria-label={`React with ${emoji}`} className="rounded-full px-1.5 py-1 text-base hover:bg-hover">
            {emoji}
          </button>
        ))}
      {canReact && (
        <ToolbarButton ref={pickerAnchor} label="Add reaction" onClick={() => setPickerOpen((v) => !v)}>
          <SmilePlus size={17} />
        </ToolbarButton>
      )}
      <ToolbarButton label="Reply" onClick={() => setReplyTo(key, message)}>
        <CornerUpLeft size={17} />
      </ToolbarButton>
      {canThread && (
        <ToolbarButton label="Create thread" onClick={startThread}>
          <Threads size={17} />
        </ToolbarButton>
      )}
      {isMine && (
        <ToolbarButton label="Edit" onClick={() => setEditing(key, message.id)}>
          <Pencil size={16} />
        </ToolbarButton>
      )}
      {(isMine || canManageMessages) && (
        <ToolbarButton label="Delete (hold Shift to skip confirmation)" danger onClick={(e) => void deleteMessage(message, e.shiftKey)}>
          <Trash2 size={16} />
        </ToolbarButton>
      )}
      <ToolbarButton ref={moreAnchor} label="More" onClick={() => setMoreOpen((v) => !v)}>
        <MoreHorizontal size={17} />
      </ToolbarButton>
    </div>
  );

  if (bubble) {
    // Long-press (or right-click) opens the message menu; with a mouse, so does the toolbar.
    return (
      <article
        id={`message-${message.id}`}
        aria-label={`${authorName}: ${ariaText}`}
        title={formatLong(message.createdAt)}
        onContextMenu={onContextMenu}
        {...swipe.handlers}
        style={swipe.style}
        data-cf="message"
        data-cf-mine={isMine || undefined}
        className={cn(
          'group relative flex gap-2.5 px-4',
          isMine && 'flex-row-reverse',
          showHeader ? 'mt-[calc(var(--cf-message-gap)*0.8)]' : 'mt-1',
          highlighted && 'animate-flash',
          local && 'opacity-60',
        )}
      >
        {swipeHint}
        {!isMine && (
          <div className="shrink-0" style={{ width: bubbleAvatar }}>
            {showHeader && (
              <button type="button" onClick={openProfile} aria-label={`View ${authorName}'s profile`} className="rounded-full">
                <Avatar user={authorLook} size={bubbleAvatar} />
              </button>
            )}
          </div>
        )}
        <div ref={bubbleRef} data-cf="bubble" className={cn('flex max-w-[min(82%,680px)] min-w-0 flex-col gap-1', isMine ? 'items-end' : 'items-start')}>
          {showHeader && !isMine && (
            <div className="flex max-w-full items-center gap-1.5 pl-3">
              <button type="button" data-cf="message-author" onClick={openProfile} className="truncate text-xs font-bold">
                <StyledName user={author} fallbackColor={roleColor}>
                  {authorName}
                </StyledName>
              </button>
              {roleName && <RoleTag name={roleName} />}
              <StaffBadge role={author?.staffRole} className="self-center" />
            </div>
          )}
          {message.replyTo && (
            <button type="button" onClick={() => onJump(message.replyTo!.id)} className="flex max-w-full items-center gap-1.5 px-3 text-left text-xs text-fg-muted">
              <CornerUpLeft size={13} className="shrink-0 -scale-x-100" />
              <ReplyPreview reply={message.replyTo} pinged={message.replyPing} serverId={message.serverId} />
            </button>
          )}
          {isEditing ? (
            <InlineEditor message={message} onDone={() => setEditing(key, null)} />
          ) : (
            <MessageBody message={message} fromMe={isMine} bubble mentionsMe={!!mentionsMe} onShowHistory={showHistory} />
          )}
          {failedNote}
          {reactions}
          {message.thread && <ThreadChip thread={message.thread} />}
          <time dateTime={message.createdAt} title={formatLong(message.createdAt)} className="sr-only">
            {formatMessageTime(message.createdAt)}
          </time>
        </div>
        {toolbar}
        {menus}
      </article>
    );
  }

  return (
    <article
      id={`message-${message.id}`}
      aria-label={`${authorName}: ${ariaText}`}
      onContextMenu={onContextMenu}
      {...swipe.handlers}
      style={swipe.style}
      data-cf="message"
      data-cf-mine={isMine || undefined}
      data-cf-mentions-me={mentionsMe || undefined}
      className={cn(
        'group relative flex gap-3.5 px-4 transition-colors sm:px-6',
        compact ? 'py-0.5' : showHeader ? 'mt-[var(--cf-message-gap)] pt-0.5 pb-0.5' : 'py-0.5',
        mentionsMe ? 'border-l-2 border-mention-bar bg-mention pl-[14px] sm:pl-[22px]' : 'hover:bg-hover/40',
        highlighted && 'animate-flash',
        local && 'opacity-60',
      )}
    >
      {swipeHint}
      {!compact && (
        <div className="shrink-0 pt-0.5" style={{ width: avatarSize }}>
          {showHeader ? (
            <button type="button" onClick={openProfile} aria-label={`View ${authorName}'s profile`} className="rounded-full transition-transform active:translate-y-px">
              <Avatar user={authorLook} size={avatarSize} />
            </button>
          ) : (
            <time dateTime={message.createdAt} title={formatLong(message.createdAt)} className="block pt-1 text-right text-[10px] text-fg-muted opacity-0 group-hover:opacity-100">
              {formatClock(message.createdAt)}
            </time>
          )}
        </div>
      )}

      <div className="min-w-0 flex-1">
        {message.replyTo && (
          <button
            type="button"
            onClick={() => onJump(message.replyTo!.id)}
            className="mb-0.5 flex max-w-full items-center gap-1.5 text-left text-[13px] text-fg-muted hover:text-fg-soft"
          >
            <CornerUpLeft size={14} className="shrink-0 -scale-x-100" />
            <ReplyPreview reply={message.replyTo} pinged={message.replyPing} serverId={message.serverId} />
          </button>
        )}

        {compact ? (
          <div className="flex items-baseline gap-2 text-[15px] leading-[22px]">
            <time dateTime={message.createdAt} title={formatLong(message.createdAt)} className="w-14 shrink-0 text-[11px] text-fg-muted tabular-nums">
              {formatClock(message.createdAt)}
            </time>
            <div className="min-w-0 flex-1">
              <button type="button" data-cf="message-author" onClick={openProfile} className="mr-1.5 font-semibold hover:underline">
                <StyledName user={author} fallbackColor={roleColor}>
                  {authorName}
                </StyledName>
              </button>
              {!isEditing && <MessageBody message={message} fromMe={isMine} inline onShowHistory={showHistory} />}
            </div>
          </div>
        ) : (
          showHeader && (
            <div className="flex items-baseline gap-2">
              <button type="button" data-cf="message-author" onClick={openProfile} className="truncate text-[15px] font-bold text-fg hover:underline">
                <StyledName user={author} fallbackColor={roleColor}>
                  {authorName}
                </StyledName>
              </button>
              {roleName && <RoleTag name={roleName} />}
              <StaffBadge role={author?.staffRole} className="self-center" />
              {timestamp}
              {message.pinnedAt && <PinnedMark />}
              {isSaved && <SavedMark remindAt={savedState} />}
            </div>
          )
        )}
        {!showHeader && !compact && (message.pinnedAt || isSaved) && (
          <span className="absolute top-1 right-3 flex gap-1 group-hover:hidden">
            {message.pinnedAt && <PinnedMark />}
            {isSaved && <SavedMark remindAt={savedState} />}
          </span>
        )}

        {isEditing ? (
          <InlineEditor message={message} onDone={() => setEditing(key, null)} />
        ) : (
          !compact && <MessageBody message={message} fromMe={isMine} onShowHistory={showHistory} />
        )}

        {failedNote}
        {reactions}
        {message.thread && <ThreadChip thread={message.thread} />}
      </div>

      {toolbar}

      {menus}
    </article>
  );
});

const ToolbarButton = ({
  label,
  onClick,
  children,
  danger,
  ref,
}: {
  label: string;
  onClick: (e: MouseEvent) => void;
  children: React.ReactNode;
  danger?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={cn('rounded-full p-1.5 transition-colors', danger ? 'text-danger hover:bg-danger-soft' : 'text-fg-muted hover:bg-hover hover:text-fg')}
  >
    {children}
  </button>
);

function MessageBody({
  message,
  fromMe,
  inline,
  bubble,
  mentionsMe,
  onShowHistory,
}: {
  message: ClientMessage;
  fromMe: boolean;
  inline?: boolean;
  bubble?: boolean;
  mentionsMe?: boolean;
  /** For people who may see what it said before: "(edited)" opens the history. */
  onShowHistory?: () => void;
}) {
  const inviteCodes = useMemo(() => (message.pending ? [] : extractInviteCodes(message.content)), [message.content, message.pending]);
  const giftCodes = useMemo(() => (message.pending ? [] : extractGiftCodes(message.content)), [message.content, message.pending]);
  const themeCodes = useMemo(() => (message.pending ? [] : extractThemeCodes(message.content)), [message.content, message.pending]);
  const showPreviews = useAuth((s) => s.settings?.showLinkPreviews ?? true);
  const previewUrls = useMemo(() => (message.pending || !showPreviews ? [] : extractPreviewUrls(message.content)), [message.content, message.pending, showPreviews]);
  // A message that's only a GIF link shows just the GIF.
  const gifOnly = previewUrls.length === 1 && isGifOnly(message.content);
  const editedClass = cn('ml-1 text-[11px] whitespace-nowrap', bubble && fromMe ? 'opacity-75' : 'text-fg-muted');
  const edited = !message.editedAt ? null : onShowHistory ? (
    <button type="button" onClick={onShowHistory} className={cn(editedClass, 'hover:underline')} title={`Edited ${formatLong(message.editedAt)}. Click to see the earlier versions.`}>
      (edited)
    </button>
  ) : (
    <span className={editedClass} title={`Edited ${formatLong(message.editedAt)}`}>
      (edited)
    </span>
  );
  return (
    <>
      {message.content &&
        !gifOnly &&
        (bubble ? (
          <div
            className={cn(
              'max-w-full rounded-[22px] px-3.5 py-2',
              fromMe
                ? 'rounded-br-md bg-accent text-accent-fg [&_.mention]:bg-accent-fg/20! [&_.mention]:text-accent-fg! [&_a]:text-accent-fg! [&_a]:underline [&_:not(pre)>code]:bg-black/15! [&_:not(pre)>code]:text-accent-fg!'
                : 'rounded-bl-md bg-sunken text-fg',
              mentionsMe && !fromMe && 'ring-2 ring-mention-bar/60',
            )}
          >
            <MessageContent content={message.content} serverId={message.serverId} mentionEveryone={message.mentionEveryone} emojiUrls={message.emojiUrls} suffix={edited} className="text-[15px] leading-[22px]" />
          </div>
        ) : (
          <MessageContent content={message.content} serverId={message.serverId} mentionEveryone={message.mentionEveryone} emojiUrls={message.emojiUrls} suffix={edited} className={cn('text-[15px] leading-[22px] text-fg', inline && 'inline')} />
        ))}
      {inviteCodes.map((code) => (
        <InviteEmbed key={code} code={code} fromMe={fromMe} />
      ))}
      {giftCodes.map((code) => (
        <GiftEmbed key={code} code={code} fromMe={fromMe} />
      ))}
      {themeCodes.map((code) => (
        <ThemeEmbed key={code} code={code} />
      ))}
      {previewUrls.map((url) => (
        <LinkEmbed key={url} url={url} />
      ))}
      {message.forwarded && <ForwardedCard forwarded={message.forwarded} />}
      {message.poll && !message.pending && <PollCard message={message} poll={message.poll} />}
      <Attachments attachments={message.attachments} />
    </>
  );
}

function ReplyPreview({ reply, pinged, serverId }: { reply: NonNullable<MessageDTO['replyTo']>; pinged: boolean; serverId: string | null }) {
  const author = useUser(reply.author?.id, reply.author);
  const authorName = useMemberName(serverId, reply.author?.id) ?? author?.displayName ?? 'Deleted User';
  const users = useUsers((s) => s.users);
  const roles = useServers((s) => s.roles);
  const text = renderMentionsAsText(reply.content, (id) => users[id]?.displayName, (id) => roles[id]?.name).replace(/\s+/g, ' ');
  const blocked = useIsBlocked(reply.author?.id);
  if (blocked) return <span className="truncate italic">Replying to a blocked message</span>;
  return (
    <>
      <Avatar user={author} size={16} />
      <span className="shrink-0 font-semibold text-fg-soft">
        {pinged ? '@' : ''}
        {authorName}
      </span>
      <span className="truncate">{text || (reply.hasAttachments ? 'Click to see attachment' : '…')}</span>
    </>
  );
}

function ReactionChip({ message, emoji, userIds, mine, canReact }: { message: MessageDTO; emoji: string; userIds: string[]; mine: boolean; canReact: boolean }) {
  const users = useUsers((s) => s.users);
  const names = userIds.map((id) => users[id]?.displayName ?? 'someone');
  const label = names.length > 3 ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more` : names.join(', ');
  return (
    <Tooltip label={`${label} reacted with ${emojiLabel(emoji)}`}>
      <button
        type="button"
        disabled={!canReact && !mine}
        onClick={() => void toggleReaction(message, emoji)}
        aria-pressed={mine}
        aria-label={`${emojiLabel(emoji)} ${userIds.length}, ${mine ? 'remove your reaction' : 'react'}`}
        className={cn(
          'flex h-7 items-center gap-1.5 rounded-full px-2.5 text-sm transition-colors',
          mine ? 'bg-accent/20 text-fg ring-1 ring-accent/40' : 'bg-well text-fg-soft hover:bg-active',
        )}
      >
        <EmojiGlyph emoji={emoji} className="text-base leading-none" />
        <span className="text-xs font-bold tabular-nums">{userIds.length}</span>
      </button>
    </Tooltip>
  );
}

function InlineEditor({ message, onDone }: { message: MessageDTO; onDone: () => void }) {
  const users = useUsers((s) => s.users);
  // Show @username, @Role and :emoji: in the editor; re-encode to tokens on save.
  const [value, setValue] = useState(() => decodeForEditing(message.content));
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [value]);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed && !message.poll && message.attachments.length === 0) return void deleteMessage(message).then(onDone);
    const byUsername = new Map(Object.values(users).map((u) => [u.username.toLowerCase(), u.id]));
    const content = encodeComposerText(trimmed, { serverId: message.serverId, usernames: byUsername });
    if (content === message.content) return onDone();
    setSaving(true);
    const ok = await editMessage(message, content);
    setSaving(false);
    if (ok) onDone();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onDone();
    } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void save();
    }
  };

  return (
    <div className="mt-1">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={saving}
        aria-label="Edit message"
        className="w-full resize-none rounded-2xl border border-line bg-elevated px-3.5 py-2 text-[15px] leading-[22px] text-fg focus:border-accent focus:outline-none"
      />
      <p className="mt-1 text-xs text-fg-muted">
        escape to{' '}
        <button type="button" onClick={onDone} className="text-link hover:underline">
          cancel
        </button>{' '}
        • enter to{' '}
        <button type="button" onClick={() => void save()} className="text-link hover:underline">
          save
        </button>
      </p>
    </div>
  );
}

/** A forwarded message: a copy of it as it was, with who wrote it, when and where. */
function ForwardedCard({ forwarded }: { forwarded: ForwardedMessageDTO }) {
  const author = { id: forwarded.author.id ?? 'deleted', displayName: forwarded.author.displayName, avatarUrl: forwarded.author.avatarUrl };
  return (
    <div className="mt-1 w-fit max-w-full min-w-[min(100%,240px)] rounded-xl border-l-4 border-line-strong bg-sunken/70 px-3 py-2">
      <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold tracking-wide text-fg-muted uppercase">
        <Forward size={12} /> Forwarded
      </p>
      <div className="flex items-center gap-2">
        <Avatar user={author} size={20} decorate={false} />
        <span className="truncate text-sm font-semibold text-fg">{author.displayName}</span>
        <time dateTime={forwarded.createdAt} title={formatLong(forwarded.createdAt)} className="shrink-0 text-[11px] text-fg-muted">
          {formatMessageTime(forwarded.createdAt)}
        </time>
      </div>
      {forwarded.content && <MessageContent content={forwarded.content} serverId={null} className="mt-0.5 text-[15px] leading-[22px] text-fg" />}
      <Attachments attachments={forwarded.attachments} />
      <p className="mt-1 text-[11px] text-fg-muted">From {forwarded.from}</p>
    </div>
  );
}

function SavedMark({ remindAt }: { remindAt: string | null | undefined }) {
  const label = remindAt ? `Saved · reminder ${formatReminder(remindAt)}` : 'Saved';
  return (
    <span title={label} className="inline-flex shrink-0 items-center self-center text-accent">
      {remindAt ? <AlarmClock size={12} aria-label={label} /> : <BookmarkFilled size={12} aria-label={label} />}
    </span>
  );
}

function PinnedMark() {
  return (
    <span title="Pinned" className="inline-flex shrink-0 items-center self-center text-fg-muted">
      <Pin size={12} aria-label="Pinned" className="rotate-45" />
    </span>
  );
}
